import { useCallback } from 'react'
import {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  useReducedMotion,
  type WithSpringConfig,
} from 'react-native-reanimated'
import { impact, ImpactStyle } from '../utils/haptics'

const PRESS_SPRING: WithSpringConfig = {
  damping: 15,
  stiffness: 150,
  mass: 0.4,
}

const RELEASE_SPRING: WithSpringConfig = {
  damping: 12,
  stiffness: 200,
  mass: 0.3,
}

interface UseAnimatedPressOptions {
  scaleTo?: number
  haptic?: (typeof ImpactStyle)[keyof typeof ImpactStyle] | false
  disabled?: boolean
}

export function useAnimatedPress(options: UseAnimatedPressOptions = {}) {
  const { scaleTo = 0.97, haptic = ImpactStyle.Light, disabled = false } = options

  const reducedMotion = useReducedMotion()
  const scale = useSharedValue(1)

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }))

  const onPressIn = useCallback(() => {
    if (disabled) return
    if (!reducedMotion) {
      scale.value = withSpring(scaleTo, PRESS_SPRING)
    }
    if (haptic !== false) {
      impact(haptic)
    }
  }, [disabled, reducedMotion, scaleTo, haptic, scale])

  const onPressOut = useCallback(() => {
    if (disabled) return
    if (!reducedMotion) {
      scale.value = withSpring(1, RELEASE_SPRING)
    }
  }, [disabled, reducedMotion, scale])

  return { animatedStyle, onPressIn, onPressOut }
}
